<template>
  <div class="profileContainer">
    <div class="my-profile">
      <div class="header">
        <h3>My Profile</h3>
      </div>
      <div v-if="!currentCustomer" class="loading-container">
        <a-icon slot="indicator" type="loading" style="font-size: 24px" spin />
      </div>
      <div v-else class="content">
        <section>
          <div class="card">
            <div class="card-body">
              <strong>- Email:</strong>
              <span class="email-label">{{
                currentUser ? currentUser.email : ""
              }}</span>
            </div>
            <div class="card-body">
              <strong>- Role:</strong>
              <input
                v-model="roleInput"
                type="text"
                id="role"
                class="input"
                :class="{
                  error: roleInput === ''
                }"
              />
              <font-awesome-icon
                @click="handleEditRoleClick"
                icon="save"
                class="icon"
                :class="{
                  errorButton: roleInput === ''
                }"
              />
            </div>
          </div>
        </section>
        <section>
          <a-avatar class="avatar">
            {{ photoName }}
          </a-avatar>
          <div class="profile-details">
            <input
              v-model="username"
              class="input"
              :style="computedWidth"
              :class="{
                error: username === ''
              }"
            />
            <font-awesome-icon
              @click="handleEditNameClick"
              icon="save"
              class="icon"
              :class="{
                errorButton: username === ''
              }"
            />
          </div>
        </section>
      </div>
      <div class="content"></div>
    </div>
    <div class="subscription-info">
      <div class="header">
        <h3>Subscription information</h3>
      </div>
      <SubscriptionInfo
        :currentCustomer="currentCustomer"
        :currentCustomerMembers="currentCustomerMembers"
      />
    </div>
  </div>
</template>

<script>
// @ is an alias to /src
import axios from "@/api/index";
import { mapState } from "vuex";
import SubscriptionInfo from "@/components/SubscriptionInfo";
import { getSessions } from "@/api/vm";
import {
  updateUser,
  getCustomer,
  getCustomerMembers,
  getMyUser
} from "@/api/users";
import { tokenRequest } from "@/config/authConfig";

export default {
  name: "Profile",
  components: { SubscriptionInfo },
  data() {
    return {
      roleInput: "",
      username: "",
      isNameEditable: false,
      isRoleEditable: false,
      isFetchingSession: true,
      currentCustomer: null,
      currentCustomerMembers: [],
      sessions: [],
      hoursForecast: 0,
      totalCreditsForThisMonth: 0,
      usedCreditsThisMonth: 0
    };
  },
  async mounted() {
    await this.getToken();
    await this.fetchSessions();
    if (this.currentUser) {
      this.roleInput = this.currentUser.role || "";
      this.username = this.currentUser.name;
      this.fetchCustomer();
      this.fetchCustomerMembers();
    }
  },
  computed: {
    computedWidth() {
      return `width: ${
        this.username.length < 15 ? 15 : this.username.length
      }ch`;
    },
    photoName() {
      const [firstName, lastName] = this.currentUser
        ? this.currentUser.name.split(" ")
        : ["", ""];
      return `${firstName ? firstName[0] : ""}${lastName ? lastName[0] : ""}`;
    },
    ...mapState({
      currentUser: state => state.currentUser
    })
  },
  watch: {
    currentUser(newVal, oldVal) {
      if (newVal && newVal.id !== oldVal?.id) {
        this.roleInput = newVal.role || "";
        this.username = newVal.name;
        this.fetchCustomer();
        this.fetchCustomerMembers();
      }
    }
  },
  methods: {
    handleEditNameClick() {
      this.updateUserDetails();
    },
    handleEditRoleClick() {
      this.updateUserDetails();
    },
    async updateUserDetails() {
      if (!this.currentUser) {
        return;
      }

      if (
        this.roleInput === this.currentUser.role &&
        this.username === this.currentUser.name
      ) {
        return;
      }

      if (this.roleInput == "" || this.username === "") {
        // this.roleInput = this.currentUser.role
        // this.username = this.currentUser.name
        return;
      }

      try {
        const response = await updateUser(this.currentUser.id, {
          name: this.username,
          role: this.roleInput,
          customerId: this.currentUser.customerId,
          isAdmin: this.currentUser.isAdmin
        });
        this.$store.commit("setCurrentUser", response.data);
        this.$notification.success({
          message: "User details successfully updated!"
        });
        this.fetchCustomerMembers();
      } catch (error) {
        this.$notification.error({
          message: error.message || "Could not update user",
          description: "Unable to update user details"
        });
      }
    },
    async fetchCustomer() {
      try {
        const response = await getCustomer(this.currentUser.customerId);
        this.currentCustomer = response.data;
      } catch (error) {
        this.$notification.error({
          message: error.message || "Could not get user",
          description: "Unable to retrieve user details"
        });
      }
    },
    async fetchCustomerMembers() {
      try {
        const response = await getCustomerMembers(this.currentUser.customerId);
        const { data = [] } = response.data;
        this.currentCustomerMembers = data;
      } catch (error) {
        this.$notification.error({
          message: error.message || "Could not get user",
          description: "Unable to retrieve user details"
        });
      }
    },
    async getToken() {
      try {
        const userData = await this.$msal.acquireToken(tokenRequest);
        axios.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${userData.accessToken}`;
        this.fetchUser();
      } catch (error) {
        this.$notification.error({
          message: error.message || "Could not get user",
          description: "Unable to retrieve user details"
        });
      }
    },
    async fetchUser() {
      try {
        const response = await getMyUser(this.accessToken);
        this.$store.commit("setCurrentUser", response.data);
        if (this.currentUser && !this.currentUser.isActive) {
          this.$msal.signOut();
        }
      } catch (error) {
        if (error && error.response && error.response.status === 403) {
          return this.$msal.signOut();
        }
        this.$notification.error({
          message: error.message || "Could not get user",
          description: "Unable to retrieve user details"
        });
      }
    },
    async fetchSessions() {
      try {
        this.isFetchingSession = true;
        const response = await getSessions();
        const {
          sessions = [],
          hoursForecast = 0,
          totalCreditsForThisMonth = 0,
          usedCreditsThisMonth = 0
        } = response.data ? response.data : {};
        this.sessions = sessions;
        this.hoursForecast = parseFloat(hoursForecast).toFixed(2);
        this.totalCreditsForThisMonth = totalCreditsForThisMonth;
        this.usedCreditsThisMonth = usedCreditsThisMonth;
        this.isFetchingSession = false;
      } catch (error) {
        this.isFetchingSession = false;

        this.$notification.error({
          message: error.message || "Could not get user",
          description: "Unable to get VM sessions"
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.profileContainer {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.header {
  height: 80px;
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding-left: $p4;
  background-color: $white;

  & h3 {
    color: #e60010;
    font-size: 24px;
    font-weight: 400;
    margin: 0;
  }
}

.input {
  border: none;
  background: transparent;
  border: 1px solid $defaultFocus;
  border-radius: 8px;
  padding-left: 4px;
  padding-bottom: 4px;
  font-size: $baseTextSize;
  width: 180px;
  margin-right: $m1;
}

.error {
  border: 1.5px solid #e60010;
}

.loading-container {
  height: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.icon {
  color: $accentColor;
  cursor: pointer;
  &:hover {
    color: $accentFocus;
  }
}

.errorButton {
  cursor: not-allowed;
  color: $defaultFocus;
  &:hover {
    color: $defaultFocus;
  }
}
.my-profile {
  width: 100%;
  background-color: $backgroundColor;

  & > .content {
    display: flex;
    justify-content: space-between;

    & > section {
      width: 50%;
      display: flex;
      flex-direction: column;
      align-items: center;

      & .avatar {
        font-size: 24px;
        background-color: #e60010;
        width: 82px;
        height: 82px;
        display: flex;
        align-items: center;
      }

      & .profile-details {
        margin-top: $m3;
        font-size: 20px;
        & > span {
          margin-right: $m1;
        }

        @media screen and (max-width: $MobileMaxWidth) {
          font-size: $textSmall;
          margin-bottom: $m2;
        }
      }

      & > .card {
        display: inherit;
        flex-direction: inherit;
        align-items: flex-start;
        justify-content: space-around;
        background-color: $white;
        width: 80%;
        max-width: 520px;
        height: 142px;
        padding-top: $p3;
        padding-right: $p3;
        padding-bottom: $p3;
        padding-left: $p2;
        font-size: 20px;
        color: $textColor;
        & > .card-body {
          display: flex;
          flex-wrap: wrap;
          align-items: center;

          & > .email-label {
            width: 300px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            text-align: left;
          }
        }

        & strong {
          margin-right: $m1;
          width: 80px;
          text-align: left;
        }
        & span {
          margin-right: $m1;
        }

        @media screen and (max-width: $MobileMaxWidth) {
          font-size: $textSmall;
        }
      }

      @media screen and (max-width: $MobileMaxWidth) {
        width: 100%;
      }
    }

    @media screen and (max-width: $MobileMaxWidth) {
      flex-direction: column-reverse;
    }
  }
}

.subscription-info {
  width: 100%;
  background-color: $backgroundColor;
}
</style>
